import { Component, OnInit, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { PaginatedResponse, PaginationInfo } from 'src/app/shared/models';
import { FobChargesService } from 'src/app/services/fob-charges/fob-charges.service';
import { FobChargesModalService } from 'src/app/services/fob-charges-modal/fob-charges-modal.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-cal-fob-charges',
  templateUrl: './cal-fob-charges.component.html',
  styleUrls: ['./cal-fob-charges.component.scss'],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ height: '0px', opacity: 0 }),
        animate('300ms ease-in', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-out', style({ height: '0px', opacity: 0 }))
      ])
    ])
  ]
})
export class CalFobChargesComponent extends DestructorComponent implements OnInit, AfterViewInit {

  // Data properties
  fobCharges: any[] = [];
  isLoading = false;

  // Pagination
  currentPage = 1;
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // Search and filters
  searchTerm = '';
  showFiltersForm = false;
  filters: Record<string, any> = {};

  // Bulk selection
  selectedFobCharges: string[] = [];

  constructor(
    private _fobChargesService: FobChargesService,
    private _fobChargesModalService: FobChargesModalService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadFobCharges();
  }

  ngAfterViewInit(): void {
    // No longer needed since we removed dropdown menus
  }

  loadFobCharges(page: number = 1, search: string = '', filters: Record<string, any> = {}): void {
    this.isLoading = true;
    this.currentPage = page;
    this.selectedFobCharges = []; // Clear selections when loading new data
    this.searchTerm = search;
    this.filters = filters;

    this.subscriptions.push(
      this._fobChargesService.GetData(page.toString(), search, filters).subscribe({
        next: (response: PaginatedResponse<any>) => {
          if (response.status && response.data) {
            this.fobCharges = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            this.currentPage = response.data.pagination.currentPage;
          } else {
            this.fobCharges = [];
            this.resetPagination();
          }
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars(); // Reset toolbar state
        },
        error: (error) => {
          console.error('Error loading fob charges:', error);
          this.fobCharges = [];
          this.resetPagination();
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars(); // Reset toolbar state
        }
      })
    );
  }

  onPageChange(page: number): void {
    this.loadFobCharges(page, this.searchTerm, this.filters);
  }

  toggleFilters(): void {
    this.showFiltersForm = !this.showFiltersForm;
  }

  applyFilters(): void {
    this.loadFobCharges(1, this.searchTerm, this.filters);
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.filters = {};
    this.showFiltersForm = false;
    this.loadFobCharges();
  }

  openAddFobChargeModal(): void {
    this._fobChargesModalService.clearSelectedFobCharge();
    const modalElement = document.getElementById('kt_modal_fob_charge_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openEditModal(fobCharge: any): void {
    this._fobChargesModalService.setSelectedFobCharge(fobCharge);
    const modalElement = document.getElementById('kt_modal_fob_charge_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  deleteFobCharge(fobCharge: any): void {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete "${fobCharge.name}". This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performDelete(fobCharge);
      }
    });
  }

  private performDelete(fobCharge: any): void {
    const fobChargeId = fobCharge._id || fobCharge.id;
    this.subscriptions.push(
      this._fobChargesService.DeleteDataById(fobChargeId).subscribe({
        next: (response: any) => {
          if (response.status) {
            Swal.fire({
              title: 'Deleted!',
              text: 'FOB charge has been deleted successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.loadFobCharges(this.currentPage, this.searchTerm, this.filters);
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to delete FOB charge.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while deleting FOB charge.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  // Event handlers for modal events
  onFobChargeAdded(): void {
    console.log('FOB charge added, refreshing list...');
    this.loadFobCharges(this.currentPage, this.searchTerm, this.filters);
  }

  onFobChargeUpdated(): void {
    console.log('FOB charge updated, refreshing list...');
    this.loadFobCharges(this.currentPage, this.searchTerm, this.filters);
  }

  // Utility methods
  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 10,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  // Track by function for ngFor performance
  trackByFn(index: number, item: any): any {
    return item._id || index;
  }

  // Bulk selection methods
  isSelected(fobChargeId: string): boolean {
    return this.selectedFobCharges.includes(fobChargeId);
  }

  isAllSelected(): boolean {
    return this.fobCharges.length > 0 && this.selectedFobCharges.length === this.fobCharges.length;
  }

  isIndeterminate(): boolean {
    return this.selectedFobCharges.length > 0 && this.selectedFobCharges.length < this.fobCharges.length;
  }

  toggleSelection(fobChargeId: string, event: any): void {
    if (event.target.checked) {
      if (!this.selectedFobCharges.includes(fobChargeId)) {
        this.selectedFobCharges.push(fobChargeId);
      }
    } else {
      this.selectedFobCharges = this.selectedFobCharges.filter(id => id !== fobChargeId);
    }
    this.toggleToolbars();
  }

  toggleAllSelection(event: any): void {
    if (event.target.checked) {
      this.selectedFobCharges = this.fobCharges.map(fobCharge => fobCharge._id);
    } else {
      this.selectedFobCharges = [];
    }
    this.toggleToolbars();
  }

  private toggleToolbars(): void {
    const toolbarBase = document.querySelector('[data-kt-fob-charge-table-toolbar="base"]');
    const toolbarSelected = document.querySelector('[data-kt-fob-charge-table-toolbar="selected"]');

    if (this.selectedFobCharges.length > 0) {
      toolbarBase?.classList.add('d-none');
      toolbarSelected?.classList.remove('d-none');
    } else {
      toolbarBase?.classList.remove('d-none');
      toolbarSelected?.classList.add('d-none');
    }
  }

  deleteSelectedFobCharges(): void {
    if (this.selectedFobCharges.length === 0) {
      return;
    }

    Swal.fire({
      title: 'Are you sure?',
      text: `You want to delete ${this.selectedFobCharges.length} selected FOB charge(s)?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete them!',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performBulkDelete();
      }
    });
  }

  private performBulkDelete(): void {
    if (this.selectedFobCharges.length === 0) {
      return;
    }

    this.subscriptions.push(
      this._fobChargesService.BulkDelete(this.selectedFobCharges).subscribe({
        next: () => {
          this.loadFobCharges();
          this.selectedFobCharges = [];
          this.toggleToolbars();
          Swal.fire('Deleted!', 'Selected FOB charges have been deleted.', 'success');
        },
        error: (error) => {
          console.error('Error deleting FOB charges:', error);
          Swal.fire('Error!', 'Failed to delete selected FOB charges.', 'error');
        }
      })
    );
  }

  // Math utility for template
  Math = Math;
}
